import { useParams, Link } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, Tag, User, Share2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getPostBySlug, getLocalizedPosts } from "@/data/blogPosts";
import { RelatedResources } from "@/components/RelatedResources";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePageSEO } from "@/hooks/usePageSEO";
import { getBlogImage } from "@/utils/blogImages";
import { BlogMarkdownRenderer } from "@/components/blog/BlogMarkdownRenderer";
import { BlogImage } from "@/components/blog/BlogImage";
import { DocumentPackBanner } from "@/components/DocumentPackBanner";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, language } = useLanguage();
  const post = slug ? getPostBySlug(slug, language as 'en' | 'fr') : undefined;
  const coverImage = slug && post ? getBlogImage(slug, post.category) : "";

  const ogImageUrl = post
    ? `https://iso27001kit.com/og-blog/${post.slug}.png`
    : "https://iso27001kit.com/og-image.png";

  // Consolidate cannibalizing duplicates: point the blog post canonical
  // at the stronger template page when they target the same query.
  const CANONICAL_OVERRIDES: Record<string, string> = {
    "iso-27001-risk-register-template-excel": "/iso-27001-risk-register-template-excel",
  };
  const canonicalPath = post
    ? (CANONICAL_OVERRIDES[post.slug] ?? `/blog/${post.slug}`)
    : undefined;

  usePageSEO({
    title: post?.metaTitle || "Blog Post Not Found | ISO27001KIT",
    description: post?.metaDescription || "ISO 27001 blog post",
    canonicalPath,
    ogType: "article",
    ogImage: ogImageUrl,
    ogAuthor: post?.author,
    articlePublishedTime: post?.publishDate,
    articleModifiedTime: post?.updatedDate || post?.publishDate,
    articleSection: post?.category,
    articleTags: post?.tags,
  });

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  if (!post) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">{t('blog.notFound')}</h1>
          <p className="text-muted-foreground mb-6">{t('blog.notFoundDesc')}</p>
          <Link to="/blog">
            <Button>{t('blog.backToBlog')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const relatedPosts = getLocalizedPosts(language as 'en' | 'fr')
    .filter(p => p.category === post.category && p.slug !== post.slug)
    .slice(0, 3);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href,
        });
      } catch { /* cancelled */ }
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Breadcrumb */}
      <div className="container mx-auto px-4 pt-6">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('blog.backToBlog')}
        </Link>
      </div>

      {/* Article Header */}
      <header className="container mx-auto px-4 pt-6 pb-8 max-w-4xl">
        <div className="flex items-center gap-2 mb-4">
          <Badge variant="secondary" className="text-xs uppercase tracking-wider">{post.category}</Badge>
          {post.featured && <Badge className="bg-accent text-accent-foreground text-xs">{t('blog.featuredBadge')}</Badge>}
        </div>

        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-6">
          {post.title}
        </h1>

        <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-6 max-w-3xl">
          {post.excerpt}
        </p>

        {/* Author & Meta */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground border-t border-b border-border py-4">
          <span className="flex items-center gap-2 font-medium text-foreground">
            <User className="h-4 w-4 text-primary" />
            {post.author}
          </span>
          <span className="text-border">|</span>
          <span className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            {formatDate(post.publishDate)}
          </span>
          <span className="text-border">|</span>
          <span className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            {post.readTime} {t('blog.readTime')}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleShare}
            className="ml-auto text-muted-foreground hover:text-foreground"
          >
            <Share2 className="h-4 w-4 mr-2" />
            {t('blog.share')}
          </Button>
        </div>
      </header>

      {/* Hero Image */}
      <div className="container mx-auto px-4 max-w-4xl mb-10">
        <BlogImage
          src={coverImage}
          alt={post.title}
          loading="eager"
          className="rounded-xl"
        />
      </div>

      {/* Content Grid */}
      <div className="container mx-auto px-4 pb-16">
        <div className="grid lg:grid-cols-[1fr_300px] gap-12 max-w-6xl mx-auto">
          {/* Main Content */}
          <article className="max-w-none">
            <BlogMarkdownRenderer content={post.content} />

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mt-12 pt-8 border-t border-border">
              <span className="text-sm font-medium text-foreground mr-2">Tags:</span>
              {post.tags.map(tag => (
                <Badge key={tag} variant="outline" className="flex items-center gap-1 text-xs">
                  <Tag className="h-3 w-3" />
                  {tag}
                </Badge>
              ))}
            </div>

            {/* Share CTA */}
            <div className="mt-10 p-6 bg-muted/50 rounded-xl border border-border text-center">
              <p className="text-lg font-semibold text-foreground mb-2">
                {language === 'fr' ? 'Cet article vous a été utile ?' : 'Found this article helpful?'}
              </p>
              <p className="text-muted-foreground text-sm mb-4">
                {language === 'fr' ? 'Partagez-le avec vos collègues.' : 'Share it with your colleagues.'}
              </p>
              <Button variant="outline" onClick={handleShare}>
                <Share2 className="h-4 w-4 mr-2" />
                {t('blog.share')}
              </Button>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="space-y-6">
            {/* Sticky wrapper */}
            <div className="lg:sticky lg:top-24 space-y-6">
              {/* CTA Card */}
              <Card className="bg-primary text-primary-foreground">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">{t('blog.sidebar.ctaTitle')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-sm text-primary-foreground/80 mb-3">
                    {t('blog.sidebar.ctaDesc')}
                  </p>
                  <Link to="/tools/gap-analysis"><Button variant="secondary" className="w-full" size="sm">{t('blog.sidebar.gapAnalysis')}</Button></Link>
                  <Link to="/tools/risk-assessment"><Button variant="secondary" className="w-full" size="sm">{t('blog.sidebar.riskAssessment')}</Button></Link>
                  <Link to="/soa-generator"><Button variant="secondary" className="w-full" size="sm">{t('blog.sidebar.soaGenerator')}</Button></Link>
                  <Link to="/tools/internal-audit"><Button variant="secondary" className="w-full" size="sm">{t('blog.sidebar.internalAudit')}</Button></Link>
                  <Link to="/tools/implementation-roadmap"><Button variant="secondary" className="w-full" size="sm">{t('blog.sidebar.roadmap')}</Button></Link>
                </CardContent>
              </Card>

              {/* Related Posts */}
              {relatedPosts.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">{t('blog.relatedPosts')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {relatedPosts.map(related => (
                      <Link
                        key={related.slug}
                        to={`/blog/${related.slug}`}
                        className="group block hover:bg-muted/50 p-3 rounded-lg transition-colors -mx-2"
                      >
                        <div className="flex gap-3">
                          <img
                            src={getBlogImage(related.slug, related.category)}
                            alt={related.title}
                            className="w-16 h-12 rounded object-cover flex-shrink-0"
                            loading="lazy"
                          />
                          <div>
                            <h4 className="font-medium text-sm line-clamp-2 group-hover:text-primary transition-colors">
                              {related.title}
                            </h4>
                            <p className="text-xs text-muted-foreground mt-1">
                              {related.readTime} {t('blog.readTime')}
                            </p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Document Pack CTA */}
              <DocumentPackBanner variant="sidebar" trackingId="blog_sidebar" />

              <RelatedResources category={post.category} tags={post.tags} />
            </div>
          </aside>
        </div>
      </div>

      {/* JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://iso27001kit.com" },
            { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://iso27001kit.com/blog" },
            { "@type": "ListItem", "position": 3, "name": post.title, "item": `https://iso27001kit.com/blog/${post.slug}` }
          ]
        })
      }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": post.title,
          "description": post.metaDescription,
          "image": [ogImageUrl],
          "author": { "@type": "Person", "name": post.author },
          "datePublished": post.publishDate,
          "dateModified": post.updatedDate || post.publishDate,
          "publisher": {
            "@type": "Organization",
            "name": "ISO27001KIT",
            "url": "https://iso27001kit.com",
            "logo": { "@type": "ImageObject", "url": "https://iso27001kit.com/og-image.png" }
          },
          "mainEntityOfPage": { "@type": "WebPage", "@id": `https://iso27001kit.com/blog/${post.slug}` },
          "articleSection": post.category,
          "keywords": post.tags.join(", ")
        })
      }} />
    </div>
  );
};

export default BlogPost;
